import { FormEvent, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { WORK_UNITS, type WorkUnitKey } from '../../lib/dailyContext'
import type { AppRole } from '../../types'

type ManagedUser = {
  id: string
  email: string
  nome: string | null
  role: AppRole
  ativo: boolean
  default_unit_key: WorkUnitKey | null
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  must_change_password: boolean
}

type CreateForm = {
  nome: string
  email: string
  role: AppRole
  default_unit_key: WorkUnitKey | ''
  password: string
}

const EMPTY_CREATE: CreateForm = {
  nome: '',
  email: '',
  role: 'operador',
  default_unit_key: '',
  password: '',
}

async function adminApi(method: 'GET' | 'POST' | 'PATCH', body?: unknown) {
  const sessionResult = await supabase.auth.getSession()
  const token = sessionResult.data.session?.access_token
  if (!token) throw new Error('Sessão expirada. Entre novamente no Suite.')

  const response = await fetch('/api/admin/users', {
    method,
    headers: {
      authorization: `Bearer ${token}`,
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  const data = await response.json() as { error?: string; users?: ManagedUser[]; ok?: boolean }
  if (!response.ok) throw new Error(data.error || 'Falha no gerenciamento de usuários.')
  return data
}

function generatePassword(length = 14) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*'
  const bytes = new Uint32Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => chars[value % chars.length]).join('')
}

function formatDate(value: string | null) {
  if (!value) return 'Nunca'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

function unitLabel(unitKey: WorkUnitKey | null) {
  if (!unitKey) return 'Não definida'
  return WORK_UNITS.find((unit) => unit.key === unitKey)?.label ?? unitKey
}

export function UsersAdmin({ onBack }: { onBack: () => void }) {
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE)
  const [editing, setEditing] = useState<ManagedUser | null>(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState<AppRole>('operador')
  const [editUnit, setEditUnit] = useState<WorkUnitKey | ''>('')
  const [editActive, setEditActive] = useState(true)
  const [resetPassword, setResetPassword] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const data = await adminApi('GET')
      setUsers(data.users ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os usuários.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    if (!term) return users
    return users.filter((user) =>
      [user.nome, user.email, user.role, unitLabel(user.default_unit_key)].some((value) => String(value ?? '').toLocaleLowerCase('pt-BR').includes(term)),
    )
  }, [users, search])

  async function createUser(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await adminApi('POST', createForm)
      setMessage(`Usuário ${createForm.email} criado. No primeiro acesso, ele deverá substituir a senha temporária.`)
      setCreateForm(EMPTY_CREATE)
      setCreateOpen(false)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar o usuário.')
    } finally {
      setSaving(false)
    }
  }

  function openEdit(user: ManagedUser) {
    setEditing(user)
    setEditName(user.nome ?? '')
    setEditRole(user.role)
    setEditUnit(user.default_unit_key ?? '')
    setEditActive(user.ativo)
    setResetPassword('')
    setError('')
    setMessage('')
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault()
    if (!editing || !editUnit) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await adminApi('PATCH', {
        id: editing.id,
        action: 'profile',
        nome: editName,
        role: editRole,
        default_unit_key: editUnit,
        ativo: editActive,
      })
      setMessage(`Perfil de ${editing.email} atualizado.`)
      setEditing(null)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar o perfil.')
    } finally {
      setSaving(false)
    }
  }

  async function applyPasswordReset() {
    if (!editing || resetPassword.length < 8) return
    if (!window.confirm(`Definir uma nova senha temporária para ${editing.email}? No próximo acesso, o usuário será obrigado a criar uma senha definitiva.`)) return

    setSaving(true)
    setError('')
    setMessage('')
    try {
      await adminApi('PATCH', { id: editing.id, action: 'password', password: resetPassword })
      setMessage(`Senha temporária de ${editing.email} redefinida. A troca será obrigatória no próximo acesso.`)
      setResetPassword('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível redefinir a senha.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="workspace-content admin-editor">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar ao painel</button>
          <p className="eyebrow">ADMINISTRAÇÃO · USUÁRIOS</p>
          <h1>Gestão de Usuários</h1>
          <p className="muted">Criação, unidade padrão, perfis, acesso e redefinição de senha em um único lugar.</p>
        </div>
        <button className="primary-button compact" onClick={() => { setCreateOpen(true); setEditing(null) }}>+ Novo usuário</button>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      <div className="editor-summary">
        <article><span>Usuários</span><strong>{users.length}</strong><small>contas cadastradas</small></article>
        <article><span>Ativos</span><strong>{users.filter((user) => user.ativo).length}</strong><small>acesso liberado</small></article>
        <article><span>Troca pendente</span><strong>{users.filter((user) => user.must_change_password).length}</strong><small>senha temporária ativa</small></article>
      </div>

      {createOpen && (
        <section className="editor-form-card">
          <div className="pending-head"><div><p className="eyebrow">NOVO USUÁRIO</p><h2>Criar acesso</h2></div><button className="text-button" onClick={() => setCreateOpen(false)}>Fechar</button></div>
          <form className="editor-form" onSubmit={createUser}>
            <label>Nome<input value={createForm.nome} onChange={(event) => setCreateForm((current) => ({ ...current, nome: event.target.value }))} required /></label>
            <label>E-mail<input type="email" value={createForm.email} onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))} required /></label>
            <label>Perfil<select value={createForm.role} onChange={(event) => setCreateForm((current) => ({ ...current, role: event.target.value as AppRole }))}><option value="operador">Operador</option><option value="gestao">Gestão</option><option value="administrador">Administrador</option></select></label>
            <label>Unidade padrão<select value={createForm.default_unit_key} onChange={(event) => setCreateForm((current) => ({ ...current, default_unit_key: event.target.value as WorkUnitKey }))} required><option value="">Selecione...</option>{WORK_UNITS.map((unit) => <option key={unit.key} value={unit.key}>{unit.label}</option>)}</select></label>
            <label className="span-2">Senha temporária<div className="input-with-action"><input value={createForm.password} onChange={(event) => setCreateForm((current) => ({ ...current, password: event.target.value }))} minLength={8} required /><button type="button" className="secondary-button compact" onClick={() => setCreateForm((current) => ({ ...current, password: generatePassword() }))}>Gerar</button></div></label>
            <div className="notice info span-2">A senha informada será temporária. No primeiro acesso, o usuário deverá criar uma senha definitiva antes de entrar no Suite.</div>
            <div className="form-actions span-2"><button type="button" className="secondary-button" onClick={() => setCreateOpen(false)}>Cancelar</button><button type="submit" className="primary-button compact" disabled={saving}>{saving ? 'Criando...' : 'Criar usuário'}</button></div>
          </form>
        </section>
      )}

      {editing && (
        <section className="editor-form-card">
          <div className="pending-head"><div><p className="eyebrow">EDITAR USUÁRIO</p><h2>{editing.email}</h2></div><button className="text-button" onClick={() => setEditing(null)}>Fechar</button></div>
          <form className="editor-form" onSubmit={saveProfile}>
            <label>Nome<input value={editName} onChange={(event) => setEditName(event.target.value)} required /></label>
            <label>Perfil<select value={editRole} onChange={(event) => setEditRole(event.target.value as AppRole)}><option value="operador">Operador</option><option value="gestao">Gestão</option><option value="administrador">Administrador</option></select></label>
            <label>Unidade padrão<select value={editUnit} onChange={(event) => setEditUnit(event.target.value as WorkUnitKey)} required><option value="">Selecione...</option>{WORK_UNITS.map((unit) => <option key={unit.key} value={unit.key}>{unit.label}</option>)}</select></label>
            <label className="checkbox-field"><input type="checkbox" checked={editActive} onChange={(event) => setEditActive(event.target.checked)} /><span>Usuário ativo — permite login e acesso conforme o perfil.</span></label>
            <div className="form-actions span-2"><button type="submit" className="primary-button compact" disabled={saving || !editUnit}>{saving ? 'Salvando...' : 'Salvar perfil'}</button></div>
          </form>

          <div className="password-reset-block">
            <div><strong>Redefinir senha</strong><span>A nova senha será temporária e exigirá troca no próximo acesso.</span></div>
            <div className="input-with-action"><input value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} placeholder="Nova senha temporária" minLength={8} /><button type="button" className="secondary-button compact" onClick={() => setResetPassword(generatePassword())}>Gerar</button><button type="button" className="danger-button compact" disabled={saving || resetPassword.length < 8} onClick={() => void applyPasswordReset()}>Redefinir</button></div>
          </div>
        </section>
      )}

      <div className="admin-table-toolbar">
        <div><h2>Usuários cadastrados</h2><p className="muted">{filtered.length} de {users.length} usuários</p></div>
        <input placeholder="Pesquisar nome, e-mail, perfil ou unidade..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      {loading ? <div className="empty-state">Carregando usuários...</div> : (
        <div className="admin-table-wrap">
          <table className="admin-data-table users-table">
            <thead><tr><th>Usuário</th><th>Perfil</th><th>Unidade</th><th>Status</th><th>Último acesso</th><th>Criado em</th><th>Ações</th></tr></thead>
            <tbody>{filtered.map((user) => (
              <tr key={user.id}>
                <td><strong>{user.nome || 'Sem nome'}</strong><small className="table-subline">{user.email}</small></td>
                <td><span className={`role-pill ${user.role}`}>{user.role}</span></td>
                <td>{unitLabel(user.default_unit_key)}</td>
                <td><span className={`status-pill ${user.ativo ? 'active' : 'inactive'}`}>{user.ativo ? 'Ativo' : 'Desativado'}</span>{user.must_change_password && <small className="table-subline">Senha temporária</small>}</td>
                <td>{formatDate(user.last_sign_in_at)}</td><td>{formatDate(user.created_at)}</td>
                <td><button className="secondary-button compact" onClick={() => openEdit(user)}>Editar</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  )
}
